import { Box, Link, Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getAdvAnnouncements } from '~/api/AdvAnnouncementsRepository'
import type { AdvAnnouncements } from '~/lib/types/AdvAnnouncements'

import { snackActions } from '~/lib/snackbarHelper'
import { SectionCardContainer } from '~/components/common/SectionCardContainer'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { logger } from '~/lib/logger'
import { ToggleViewButton } from '~/components/common/ToggleButton'

const useAnnouncements = (announcements: AdvAnnouncements) => {
  const [isMoreView, setIsMoreView] = useState(false)
  const handleClick = useCallback(() => {
    setIsMoreView((prev) => !prev)
  }, [])
  const displayedAnnouncements = useMemo(() => {
    return isMoreView ? announcements : announcements.slice(0, 3)
  }, [announcements, isMoreView])

  return { handleClick, displayedAnnouncements, isMoreView }
}

export const AdvAnnouncementAlert: React.FC = () => {
  const [advAnnouncements, setAdvAnnouncements] = useState<AdvAnnouncements | 'loading'>('loading')

  useEffect(() => {
    ;(async () => {
      try {
        const advAnnouncements = await getAdvAnnouncements()
        setAdvAnnouncements(advAnnouncements)
      } catch (e) {
        logger.error(new Error('Failed to get adv announcements', { cause: e }))
        snackActions.error('お知らせの読み込みに失敗しました')
        setAdvAnnouncements([])
      }
    })()
  }, [])

  const { handleClick, displayedAnnouncements, isMoreView } = useAnnouncements(
    advAnnouncements === 'loading' ? [] : advAnnouncements,
  )

  if (advAnnouncements === 'loading' || !advAnnouncements.length) return null

  return (
    <SectionCardContainer
      icon={<NotificationsIcon sx={{ fontSize: 24, color: '#F29D38' }} />}
      title="お知らせ"
      showAnimation
    >
      <Stack component="ul" gap={1} m={0} p={2} sx={{ listStyle: 'disc', pl: 4 }}>
        {displayedAnnouncements.map((advAnnouncement) => (
          <Box component="li" key={advAnnouncement.url}>
            <Link
              href={advAnnouncement.url}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{
                color: 'primary.main',
                overflowWrap: 'break-word',
                lineHeight: 1.5,
              }}
              variant="labelSJpNormal"
            >
              {advAnnouncement.title}
            </Link>
            {advAnnouncement.isEmphasized && (
              <Typography
                component="span"
                variant="labelSJpTight"
                sx={{
                  border: '1px solid',
                  borderColor: 'currentColor',
                  borderRadius: 2,
                  color: 'error.main',
                  fontWeight: 500,
                  px: 1,
                  py: 0.5,
                  ml: 1,
                }}
              >
                重要
              </Typography>
            )}
          </Box>
        ))}
      </Stack>

      <ToggleViewButton
        length={advAnnouncements.length}
        handleClick={handleClick}
        isMoreView={isMoreView}
        toggleButtonThreshold={3}
      />
    </SectionCardContainer>
  )
}
